import { yupResolver } from '@hookform/resolvers/yup'
import Alert from 'components/UI/Alert'
import InfoBlock from 'components/UI/Blocks/InfoBlock'
import Button from 'components/UI/Buttons/Button'
import Select from 'components/UI/Select'
import Typography from 'components/UI/Typography'
import Heading from 'components/UI/Typography/Heading'
import { useAppSelector } from 'hooks'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { SingleValue } from 'react-select'
import { useGetNotaryCitiesQuery, useLazyGetNotariesQuery, useSetNotaryMutation } from 'redux/api/pledgeApi'
import { getCurrentFlowId } from 'redux/store/slices/userSlice'
import { ApplicationEvents } from 'share/enums/events'
import { NotaryCityUnit } from 'share/types/api/pledge'
import tw from 'twin.macro'
import Emitter from 'utils/emitter'
import * as yup from 'yup'

const schema = yup
  .object({
    city: yup
      .object()
      .shape({
        id: yup.string().required(),
        city_obj: yup.string().notRequired(),
        region: yup.string().notRequired(),
        city: yup.string().notRequired(),
        district: yup.string().notRequired(),
        street: yup.string().notRequired(),
        house_number: yup.string().notRequired(),
        krisha_address: yup.string().notRequired(),
        kato_code: yup.string().nullable(),
        corpus: yup.string().nullable(),
        apartment_number: yup.string().nullable(),
        street_code: yup.string().nullable()
      })
      .required('city required'),
    notary: yup
      .object()
      .shape({
        id: yup.string().required(),
        phone: yup.string().required(),
        fio: yup.string().required(),
        address: yup.object().required()
      })
      .required('notary required')
  })
  .required()

type FormValues = yup.InferType<typeof schema>

const NotarySelection = () => {
  // Tools
  const { t } = useTranslation()

  // Selectors
  const currentFlowId = useAppSelector(getCurrentFlowId)

  // Computed values

  // Queries
  const { data: notaryCities, isLoading: isNotaryCitiesLoading } = useGetNotaryCitiesQuery()
  const [fetchNotaries, { data: notaries, isLoading: isNotariesLoading }] = useLazyGetNotariesQuery()

  // Mutations
  const [setNotary, { isLoading: isSetNotaryLoading }] = useSetNotaryMutation()

  // Form
  const { control, formState, reset, watch, handleSubmit } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      city: undefined,
      notary: undefined
    }
  })

  const city = watch('city')

  const { isValid } = formState

  const handleCityChange = async (city: SingleValue<NotaryCityUnit>) => {
    try {
      if (!city) return
      // @ts-ignore
      reset(values => ({ city: values.city, notary: undefined }))
      await fetchNotaries({
        city_obj_id: city.city_obj
      }).unwrap()
    } catch (err) {
      console.log(err)
    }
  }

  const handleConfirmation = async (data: FormValues) => {
    try {
      if (!currentFlowId) return
      await setNotary({
        notary_id: data.notary.id,
        credit_id: currentFlowId
      })
      Emitter.emit(ApplicationEvents.NAVIGATE_TO_NEXT_PAGE)
    } catch (err) {
      console.log(err)
    }
  }

  return (
    <InfoBlock twStyle={tw`flex flex-col gap-4 mt-4 lg:gap-y-8`}>
      <Heading variant='body' twStyle={tw`leading-5`}>
        {t('choose-notary')}
      </Heading>
      <Select
        placeholder={t('city')}
        control={control}
        name='city'
        options={notaryCities}
        isLoading={isNotaryCitiesLoading}
        isDisabled={isNotaryCitiesLoading}
        getOptionLabel={option => option.city}
        getOptionValue={option => option.city_obj}
        onChange={handleCityChange}
      />
      <Select
        key={city?.id}
        placeholder={t('flow-notary-select-placeholder')}
        control={control}
        name='notary'
        options={notaries}
        isLoading={isNotariesLoading}
        isDisabled={isNotariesLoading || !notaries}
        getOptionValue={option => option.id}
        getOptionLabel={option => option.full_address}
      />
      <Alert>
        <Typography variant='subbody' text>
          {t('flow-notary-warning')}
        </Typography>
      </Alert>
      <Button
        tw='w-full md:max-w-[fit-content] md:self-end'
        disabled={!isValid}
        loading={isSetNotaryLoading}
        onClick={handleSubmit(handleConfirmation)}
      >
        <Typography>{t('flow-aprove')}</Typography>
      </Button>
    </InfoBlock>
  )
}

export default NotarySelection
